import axios from 'axios';
import { tokenManager } from '../api/apiClient';
import config from '../config';

/**
 * Configuración global de Axios para manejar errores de autenticación
 * Este interceptor se aplica a TODAS las instancias de axios, incluso
 * las llamadas directas que no usan apiV0 o apiV1
 *
 * INCLUYE AUTO-REFRESH DE TOKEN para mantener sesión persistente
 */

// ============================================
// SISTEMA DE COLA PARA AUTO-REFRESH
// ============================================

let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Suscribe un callback para ser ejecutado cuando el token se refresque
 */
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

/**
 * Ejecuta todos los callbacks suscritos con el nuevo token
 */
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

/**
 * Solicita un nuevo token al endpoint /api/usuarios/refresh-token
 */
const refreshTokenRequest = async () => {
  const currentToken = tokenManager.getToken();
  if (!currentToken) {
    throw new Error('No token disponible para refrescar');
  }

  const response = await axios.post(
    `${config.API_BASE_URL}/api/usuarios/refresh-token`,
    { token: currentToken }
  );

  const newToken = response.data.token;
  tokenManager.setToken(newToken);

  // Actualizar user en localStorage con el nuevo token
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    const userData = JSON.parse(storedUser);
    userData.token = newToken;
    localStorage.setItem('user', JSON.stringify(userData));
  }

  return newToken;
};

// ============================================
// INTERCEPTOR DE RESPONSE CON AUTO-REFRESH
// ============================================

/**
 * Interceptor de response global que:
 * 1. Detecta errores 401 (token expirado)
 * 2. INTENTA refrescar el token automáticamente
 * 3. Solo si el refresh falla, redirige al login
 */
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si es error 401 (No autorizado) o 403 (Token expirado)
    if (error.response?.status === 401 || error.response?.status === 403) {
      // No intentar refresh en endpoints públicos
      if (
        originalRequest.url?.includes('/login') ||
        originalRequest.url?.includes('/refresh-token') ||
        originalRequest.url?.includes('/verify-token') ||
        originalRequest.url?.includes('/register') ||
        originalRequest.url?.includes('/send-code')
      ) {
        return Promise.reject(error);
      }

      // Evitar loops infinitos de retry
      if (originalRequest._retry) {
        console.error(`❌ Error ${error.response.status} - Retry ya intentado, abortando`);
        return Promise.reject(error);
      }

      // Si ya se está refrescando el token, esperar al resultado
      if (isRefreshing) {
        console.log('⏳ Token refresh en progreso, esperando...');
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            originalRequest._retry = true;
            resolve(axios(originalRequest));
          });
        });
      }

      // Marcar que estamos refrescando
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log(`🔄 Token expirado o inválido (${error.response.status}) - Intentando auto-refresh...`);

        // INTENTAR REFRESH AUTOMÁTICO
        const newToken = await refreshTokenRequest();
        isRefreshing = false;

        console.log('✅ Token refrescado exitosamente');

        // Notificar a todas las requests en espera
        onTokenRefreshed(newToken);

        // Reintentar request original con nuevo token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);

      } catch (refreshError) {
        // SI EL REFRESH FALLA, ENTONCES SÍ limpiar y redirigir
        isRefreshing = false;
        refreshSubscribers = [];

        console.error(`❌ Error ${error.response.status} - Refresh falló, cerrando sesión`);
        console.error('Detalles:', refreshError.response?.data || refreshError.message);

        // Limpiar token y datos de sesión
        tokenManager.clearToken();
        localStorage.removeItem('user');

        // Redirigir a login si no estamos ya en esa página
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          // Guardar la URL actual para redirigir después del login
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath !== '/' && currentPath !== '/login') {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
          }

          console.log('🚪 Redirigiendo a login...');
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    // Si es error 500 (Error del servidor)
    if (error.response?.status === 500) {
      console.error('❌ Error 500 - Error del servidor:', error.response.data);
    }

    return Promise.reject(error);
  }
);

/**
 * Interceptor de request global para adjuntar token automáticamente
 * Solo para requests que no sean a endpoints públicos
 */
axios.interceptors.request.use(
  (config) => {
    // No adjuntar token para endpoints públicos
    const publicEndpoints = ['/login', '/refresh-token', '/verify-token', '/register', '/send-code'];
    const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));

    if (!isPublicEndpoint) {
      const token = tokenManager.getToken();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

console.log('✅ Axios global configurado con interceptores de autenticación');

export default axios;
